import { Injectable, ElementRef } from '@angular/core';
import { ChildTableColumn, ChildTableConfig, ChildTableRow } from '../models/child-table.models';
import { ChildTableStateService } from './child-table-state.service';
import { ChildTableA11yService } from './child-table-a11y.service';
import { ChildTableSelectionService } from './child-table-selection.service';

/** Payload emitted when a cell value changes. */
export interface CellChangeEvent {
  rowId: string;
  columnKey: string;
  value: any;
  oldValue: any;
}

/** Payload emitted when cell validation fails. */
export interface ValidationErrorEvent {
  rowId: string;
  columnKey: string;
  error: string;
}

/**
 * Result of a save-edit operation.
 * The component inspects this to decide which events to emit and whether to call markForCheck.
 */
export interface SaveEditResult {
  saved: boolean;
  cellChange?: CellChangeEvent;
  validationError?: ValidationErrorEvent;
  /** Position of the cell that was being edited (for focus restoration). */
  editPosition?: { rowIndex: number; columnIndex: number };
}

/**
 * Manages inline cell editing state and operations for the child table.
 *
 * Component-scoped (provided via the host component's `providers` array).
 * Methods that would emit component events instead return event payloads
 * so the component can emit them.
 */
@Injectable()
export class ChildTableEditingService<T = any> {
  editingCell: { rowId: string; columnKey: string } | null = null;
  editingValue: any = null;

  /**
   * Guard: prevents onTableFocusOut from nuking state during the brief
   * moment between cell-editor destruction and refocus.
   */
  _editTransitionActive = false;

  constructor(
    private readonly stateService: ChildTableStateService<T>,
    private readonly a11yService: ChildTableA11yService,
    private readonly selectionService: ChildTableSelectionService,
  ) {}

  isEditing(rowId: string, columnKey: string): boolean {
    return this.editingCell?.rowId === rowId && this.editingCell?.columnKey === columnKey;
  }

  /**
   * Start inline editing a cell.
   * Returns true if editing was started, false if blocked (readonly, paste-lock, etc.).
   */
  startEditing(
    rowId: string,
    columnKey: string,
    config: ChildTableConfig<T>,
    columns: ChildTableColumn<T>[],
    displayRows: ChildTableRow<T>[],
    visibleRows: ChildTableRow<T>[],
    visibleColumns: ChildTableColumn<T>[],
    focusedCellSetter: (pos: { rowIndex: number; columnIndex: number }) => void,
    showReadonlyFieldAlert: (label: string) => void,
    pasteErrorLocked: boolean,
  ): boolean {
    if (pasteErrorLocked) return false;
    const column = columns.find((c) => c.key === columnKey);
    if (!column?.editable || config.readonly) {
      if (column && !column.editable) {
        showReadonlyFieldAlert(column.label);
      }
      return false;
    }
    const row = displayRows.find((r) => r.id === rowId);
    if (!row) return false;

    const rowIndex = visibleRows.findIndex((r) => r.id === rowId);
    const colIndex = visibleColumns.findIndex((c) => c.key === columnKey);
    if (rowIndex >= 0 && colIndex >= 0) {
      focusedCellSetter({ rowIndex, columnIndex: colIndex });
      this.selectionService.cellRange = {
        anchorRow: rowIndex, anchorCol: colIndex,
        focusRow: rowIndex, focusCol: colIndex,
      };
    }

    this.editingCell = { rowId, columnKey };
    this.editingValue = (row.data as any)[columnKey];
    this.stateService.startEditing(rowId, columnKey);

    const editorType = this.getEditorTypeLabel(column);
    const formatted = this.formatValueForA11y(this.editingValue, column);
    this.a11yService.announceEditStarted(column.label, editorType, formatted || undefined);
    return true;
  }

  /**
   * Read the current editor value from the DOM to handle cases where
   * ngModel hasn't propagated (OnPush, click-away). For menu-based editors
   * (select/multiselect) that have no input element, reads from the
   * trigger button's data-value attribute.
   */
  syncEditingValueFromDom(
    rowId: string,
    columnKey: string,
    columns: ChildTableColumn<T>[],
    tableElement: ElementRef<HTMLElement>,
  ): void {
    if (!tableElement?.nativeElement) return;
    const col = columns.find(c => c.key === columnKey);
    const cell = tableElement.nativeElement.querySelector(
      `.grid-cell[data-row-id="${rowId}"][data-col-key="${columnKey}"]`
    );
    const inputEl = cell?.querySelector('input.cell-input, textarea.cell-input') as HTMLInputElement;
    if (inputEl) {
      const isNumeric = col?.type === 'number' || col?.type === 'currency';
      this.editingValue = isNumeric
        ? (inputEl.value === '' ? null : Number(inputEl.value))
        : inputEl.value;
      return;
    }
    if (col?.type === 'select' || col?.type === 'multiselect') {
      const triggerEl = cell?.querySelector('.cell-select-trigger, .cell-listbox-trigger') as HTMLElement;
      if (!triggerEl) return;
      this.editingValue = triggerEl.getAttribute('data-value') ?? '';
    }
  }

  /**
   * Save the current edit without refocusing the cell.
   * Returns a result object so the component can emit events and restore focus.
   */
  saveEditQuietly(
    displayRows: ChildTableRow<T>[],
    visibleRows: ChildTableRow<T>[],
    visibleColumns: ChildTableColumn<T>[],
    columns: ChildTableColumn<T>[],
    tableElement: ElementRef<HTMLElement>,
    skipDomSync = false,
  ): SaveEditResult {
    if (!this.editingCell) return { saved: false };
    const { rowId, columnKey } = this.editingCell;

    if (!skipDomSync) {
      this.syncEditingValueFromDom(rowId, columnKey, columns, tableElement);
    }

    const oldRow = displayRows.find((r) => r.id === rowId);
    const oldValue = oldRow ? (oldRow.data as any)[columnKey] : null;
    const error = this.stateService.validateCell(rowId, columnKey, this.editingValue);
    if (error) {
      this.stateService.setCellError(rowId, columnKey, error);
      this.a11yService.announceValidationError(error);
      return {
        saved: false,
        validationError: { rowId, columnKey, error },
      };
    }
    this.stateService.setCellError(rowId, columnKey, null);
    this.stateService.updateCell(rowId, columnKey, this.editingValue);

    const cellChange: CellChangeEvent = { rowId, columnKey, value: this.editingValue, oldValue };

    const editRowIdx = visibleRows.findIndex((r) => r.id === rowId);
    const editColIdx = visibleColumns.findIndex((c) => c.key === columnKey);

    this._editTransitionActive = true;
    this.editingCell = null;
    this.editingValue = null;
    this.stateService.stopEditing();

    const editPosition = editRowIdx >= 0 && editColIdx >= 0
      ? { rowIndex: editRowIdx, columnIndex: editColIdx }
      : undefined;

    if (editPosition) {
      this.selectionService.cellRange = {
        anchorRow: editPosition.rowIndex, anchorCol: editPosition.columnIndex,
        focusRow: editPosition.rowIndex, focusCol: editPosition.columnIndex,
      };
    }

    // Safety net: clear transition flag after current event cycle.
    // saveEdit/cancelEdit wrappers clear it explicitly in their setTimeout;
    // this covers click-away paths that call saveEditQuietly directly.
    setTimeout(() => { this._editTransitionActive = false; }, 0);

    return { saved: true, cellChange, editPosition };
  }

  /**
   * Save the current edit and return the cell coordinates for focus restoration.
   * Returns null if save failed.
   */
  saveEdit(
    displayRows: ChildTableRow<T>[],
    visibleRows: ChildTableRow<T>[],
    visibleColumns: ChildTableColumn<T>[],
    columns: ChildTableColumn<T>[],
    tableElement: ElementRef<HTMLElement>,
  ): SaveEditResult {
    const savedRowId = this.editingCell?.rowId;
    const savedColumnKey = this.editingCell?.columnKey;
    const result = this.saveEditQuietly(displayRows, visibleRows, visibleColumns, columns, tableElement, true);
    if (!result.saved) return result;
    return {
      ...result,
      // Pass the original rowId/columnKey for DOM-based focus restoration
      editPosition: savedRowId && savedColumnKey
        ? (result.editPosition ?? undefined)
        : undefined,
    };
  }

  /**
   * Cancel the current edit and restore the original value.
   * Returns the cell coordinates for focus restoration.
   */
  cancelEdit(
    visibleRows: ChildTableRow<T>[],
    visibleColumns: ChildTableColumn<T>[],
  ): { rowIndex: number; columnIndex: number } | null {
    if (!this.editingCell) return null;

    const { rowId, columnKey } = this.editingCell;
    const editRowIdx = visibleRows.findIndex((r) => r.id === rowId);
    const editColIdx = visibleColumns.findIndex((c) => c.key === columnKey);

    this._editTransitionActive = true;
    this.editingCell = null;
    this.editingValue = null;
    this.stateService.cancelEditing();
    this.a11yService.announceEditCancelled();

    if (editRowIdx >= 0 && editColIdx >= 0) {
      this.selectionService.cellRange = {
        anchorRow: editRowIdx, anchorCol: editColIdx,
        focusRow: editRowIdx, focusCol: editColIdx,
      };
      return { rowIndex: editRowIdx, columnIndex: editColIdx };
    }
    return null;
  }

  /**
   * Handle a save event from a cell editor component.
   * Sets the editing value and delegates to saveEdit.
   */
  onCellEditorSave(
    value: any,
    displayRows: ChildTableRow<T>[],
    visibleRows: ChildTableRow<T>[],
    visibleColumns: ChildTableColumn<T>[],
    columns: ChildTableColumn<T>[],
    tableElement: ElementRef<HTMLElement>,
  ): SaveEditResult {
    this.editingValue = value;
    return this.saveEdit(displayRows, visibleRows, visibleColumns, columns, tableElement);
  }

  /**
   * Handle a Tab-save event from a cell editor component.
   * Returns the result plus next column index to focus.
   */
  onCellEditorTabSave(
    event: { value: any; shiftKey: boolean },
    colIndex: number,
    displayRows: ChildTableRow<T>[],
    visibleRows: ChildTableRow<T>[],
    visibleColumns: ChildTableColumn<T>[],
    columns: ChildTableColumn<T>[],
    tableElement: ElementRef<HTMLElement>,
  ): SaveEditResult & { nextColIndex?: number; rowIndex?: number } {
    this.editingValue = event.value;
    const result = this.saveEditQuietly(displayRows, visibleRows, visibleColumns, columns, tableElement, true);
    if (!result.saved) return result;
    const nextCol = event.shiftKey ? colIndex - 1 : colIndex + 1;
    const rowIndex = result.editPosition?.rowIndex;
    const nextColIndex = nextCol >= 0 && nextCol < visibleColumns.length ? nextCol : colIndex;
    return { ...result, nextColIndex, rowIndex };
  }

  private getEditorTypeLabel(column: ChildTableColumn<T>): string {
    switch (column.type) {
      case 'text': return 'text input';
      case 'number': return 'number input';
      case 'currency': return 'number input';
      case 'date': return 'date input';
      case 'select': return 'dropdown';
      case 'checkbox': return 'checkbox';
      default: return 'text input';
    }
  }

  private formatValueForA11y(value: any, column: ChildTableColumn<T>): string {
    if (value === null || value === undefined || value === '') return '';
    switch (column.type) {
      case 'checkbox': return value ? 'checked' : 'unchecked';
      case 'number': return String(value);
      default: return String(value);
    }
  }
}
